// ============================================================================
// PRODUCT HOOKS
// ============================================================================

import { useState, useEffect, useCallback } from 'react';
import { productService } from '../../api/services/productService';
import type {
  Product,
  ProductListItem,
  CreateProductRequest,
  UpdateProductRequest,
  ProductSearchRequest,
} from '../../api/types/product.types';
import type { ID } from '../../api/types';

export const useProducts = (params?: ProductSearchRequest) => {
  const [data, setData] = useState<ProductListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    pageSize: 20,
    totalPages: 0,
    totalItems: 0,
    hasNextPage: false,
    hasPreviousPage: false,
  });

  const paramsKey = JSON.stringify(params);

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await productService.getAll(params);
      setData(response.data);
      setPagination(response.pagination);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramsKey]);

  useEffect(() => { fetchProducts(); }, [fetchProducts]);

  return { data, loading, error, pagination, refetch: fetchProducts };
};

export const useProduct = (id: ID | undefined) => {
  const [data, setData] = useState<Product | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchProduct = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const response = await productService.getById(id);
      setData(response.data);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { fetchProduct(); }, [fetchProduct]);

  return { data, loading, error, refetch: fetchProduct };
};

export const useCreateProduct = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const createProduct = async (
    data: CreateProductRequest,
    options?: { onSuccess?: (product: Product) => void; onError?: (error: Error) => void }
  ) => {
    setLoading(true); setError(null);
    try {
      const response = await productService.create(data);
      options?.onSuccess?.(response.data);
      return response.data;
    } catch (err) {
      const e = err as Error;
      setError(e); options?.onError?.(e); throw e;
    } finally { setLoading(false); }
  };

  return { createProduct, loading, error };
};

export const useUpdateProduct = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const updateProduct = async (
    id: ID, data: UpdateProductRequest,
    options?: { onSuccess?: (product: Product) => void; onError?: (error: Error) => void }
  ) => {
    setLoading(true); setError(null);
    try {
      const response = await productService.update(id, data);
      options?.onSuccess?.(response.data);
      return response.data;
    } catch (err) {
      const e = err as Error;
      setError(e); options?.onError?.(e); throw e;
    } finally { setLoading(false); }
  };

  return { updateProduct, loading, error };
};

export const useDeleteProduct = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const deleteProduct = async (
    id: ID,
    options?: { onSuccess?: () => void; onError?: (error: Error) => void }
  ) => {
    setLoading(true); setError(null);
    try {
      await productService.delete(id);
      options?.onSuccess?.();
    } catch (err) {
      const e = err as Error;
      setError(e); options?.onError?.(e); throw e;
    } finally { setLoading(false); }
  };

  return { deleteProduct, loading, error };
};
